import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '@et/environment';
import { isOnline } from '@et/utils';

/** Mirrors the server's `emailAndPassword.minPasswordLength` (src/auth.ts, better-auth-standalone). */
const MIN_PASSWORD_LENGTH = 12;

function passwordsMatchValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    if (!confirmPassword) {
      return null;
    }
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  };
}

@Component({
  selector: 'auth-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss'],
  standalone: false,
})
export class SetPasswordComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);

  readonly minPasswordLength = MIN_PASSWORD_LENGTH;

  form: UntypedFormGroup = this.fb.group(
    {
      newPassword: [
        null,
        [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)],
      ],
      confirmPassword: [null, [Validators.required]],
    },
    { validators: passwordsMatchValidator() },
  );

  submitted = false;
  invalidToken = false;
  complete = false;
  private token: string | null = null;

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token');
    if (!this.token) {
      this.invalidToken = true;
    }
  }

  async setPassword(newPassword: string) {
    if (!this.token) {
      this.invalidToken = true;
      return;
    }
    if (!isOnline()) {
      this.form.get('newPassword')?.setErrors({ isOffline: true });
      return;
    }
    this.submitted = true;
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.betterAuth.baseUrl}/api/auth/onboarding/set-password`,
          { newPassword, token: this.token },
          { withCredentials: true },
        ),
      );
      this.complete = true;
    } catch {
      this.invalidToken = true;
    } finally {
      this.submitted = false;
    }
  }

  goToLogin() {
    this.router.navigateByUrl('/auth/login');
  }
}
